import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause, RotateCcw, Send, Headphones, Music2 } from "lucide-react";
import { toast } from "sonner";

const WORD_RE = /([A-Za-z']+)/g;
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function grade(target, guess) {
  const t = (target.toLowerCase().match(WORD_RE) || []);
  const g = (guess.toLowerCase().match(WORD_RE) || []);
  if (t.length === 0) return { accuracy: 0, missed: [] };
  const counts = {};
  t.forEach((w) => (counts[w] = (counts[w] || 0) + 1));
  let hits = 0;
  g.forEach((w) => { if (counts[w] > 0) { hits++; counts[w]--; } });
  const missed = Object.entries(counts).filter(([, c]) => c > 0).map(([w]) => w);
  return { accuracy: hits / t.length, missed };
}

function pickMime() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  if (typeof MediaRecorder === "undefined") return null;
  return candidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
}

export default function VoiceMode({ sectionText, songId, hasBackingTrack, onScore }) {
  const [supported, setSupported] = useState(true);
  const [state, setState] = useState("idle"); // idle | recording | recorded | uploading | graded
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [accuracy, setAccuracy] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [useBacking, setUseBacking] = useState(true);
  const [speed, setSpeed] = useState(1);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const audioRef = useRef(null);
  const backingRef = useRef(null);

  const backingSrc = hasBackingTrack && songId ? `${API_BASE}/songs/${songId}/backing-track` : "";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasRec = typeof MediaRecorder !== "undefined";
    setSupported(hasMedia && hasRec);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        blobRef.current = blob;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(URL.createObjectURL(blob));
        setState("recorded");
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (backingRef.current) { backingRef.current.pause(); backingRef.current.currentTime = 0; }
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setState("recording");
      setSeconds(0);
      // Start backing track alongside mic
      if (backingSrc && useBacking && backingRef.current) {
        try {
          backingRef.current.currentTime = 0;
          backingRef.current.playbackRate = speed;
          await backingRef.current.play();
        } catch (err) { console.warn("backing track autoplay blocked", err); }
      }
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= 90) { stop(); return 90; }
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error("Microphone permission denied. Please allow access and try again.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    if (backingRef.current) { backingRef.current.pause(); }
  };

  const retake = () => {
    setState("idle"); setTranscript(""); setAccuracy(null); setSeconds(0);
    if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(""); }
    blobRef.current = null;
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };

  const submit = async () => {
    if (!blobRef.current) return;
    setState("uploading");
    try {
      const form = new FormData();
      const ext = (blobRef.current.type.includes("mp4") ? "m4a"
                 : blobRef.current.type.includes("ogg") ? "ogg" : "webm");
      form.append("file", blobRef.current, `clip.${ext}`);
      const { data } = await api.post("/voice/transcribe", form);
      const text = data.text || "";
      setTranscript(text);
      const { accuracy: acc, missed } = grade(sectionText, text);
      setAccuracy(acc);
      setState("graded");
      onScore?.(acc, missed);
    } catch (e) {
      toast.error(formatApiError(e));
      setState("recorded");
    }
  };

  if (!supported) {
    return (
      <div className="p-4 rounded-xl bg-muted text-sm" data-testid="voice-unsupported">
        Your browser doesn't support microphone recording. Try Chrome, Edge, or Safari on desktop.
      </div>
    );
  }

  return (
    <div data-testid="voice-mode">
      <p className="text-sm text-muted-foreground mb-6">
        Sing this section into your mic. Play it back through your headphones, then check yourself against the lyric.
      </p>

      {/* Backing track toggle */}
      {backingSrc && (
        <div className="mb-4 flex items-center justify-between gap-4 rounded-2xl border border-border bg-secondary/40 px-5 py-3 flex-wrap"
             data-testid="voice-backing-panel">
          <div className="flex items-center gap-3">
            <Music2 size={16} className="text-primary" />
            <div>
              <p className="text-sm font-medium">Play backing track while I sing</p>
              <p className="text-xs text-muted-foreground">Use headphones so the mic only hears your voice.</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1" data-testid="voice-speed-group">
              <span className="overline-label mr-1">Speed</span>
              {[0.75, 1, 1.25].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  className={`font-mono text-xs px-2 py-1 rounded-full border transition-colors ${
                    speed === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`voice-speed-${String(s).replace(".", "-")}x`}
                >
                  {s}×
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={useBacking}
                onChange={(e) => setUseBacking(e.target.checked)}
                className="w-4 h-4 accent-primary"
                data-testid="voice-backing-toggle"
              />
              <span className="overline-label">{useBacking ? "On" : "Off"}</span>
            </label>
          </div>
        </div>
      )}

      {/* Recorder */}
      <div className="border border-border rounded-2xl p-6 bg-background">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${
                state === "recording" ? "bg-destructive text-destructive-foreground animate-pulse" : "bg-primary text-primary-foreground"
              }`}
              data-testid="voice-status-icon"
            >
              <Mic size={22} />
            </span>
            <div>
              <p className="overline-label">
                {state === "recording" ? "Listening…" :
                 state === "recorded" ? "Ready to send" :
                 state === "uploading" ? "Transcribing…" :
                 state === "graded" ? "Result" : "Ready"}
              </p>
              <p className="font-serif text-3xl tabular-nums" data-testid="voice-timer">
                {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {state === "idle" && (
              <Button onClick={start} className="rounded-full h-11 px-5" data-testid="voice-record-btn">
                <Mic size={16} className="mr-1.5" /> Start singing
              </Button>
            )}
            {state === "recording" && (
              <Button onClick={stop} variant="destructive" className="rounded-full h-11 px-5" data-testid="voice-stop-btn">
                <Square size={16} className="mr-1.5" /> Stop
              </Button>
            )}
            {(state === "recorded" || state === "graded") && (
              <>
                <Button variant="outline" onClick={togglePlay} className="rounded-full h-11 px-5" data-testid="voice-play-btn">
                  {playing ? <><Pause size={16} className="mr-1.5" /> Pause</> : <><Play size={16} className="mr-1.5" /> Play back</>}
                </Button>
                <Button variant="ghost" onClick={retake} className="rounded-full h-11" data-testid="voice-retake-btn">
                  <RotateCcw size={16} className="mr-1.5" /> Retake
                </Button>
              </>
            )}
            {state === "recorded" && (
              <Button onClick={submit} className="rounded-full h-11 px-5" data-testid="voice-submit-btn">
                <Send size={16} className="mr-1.5" /> Check my singing
              </Button>
            )}
            {state === "uploading" && (
              <Button disabled className="rounded-full h-11 px-5" data-testid="voice-uploading-btn">
                Transcribing…
              </Button>
            )}
          </div>
        </div>

        {blobUrl && (
          <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
            <Headphones size={16} />
            <span>Playback uses your default output — plug in headphones for the cleanest check.</span>
          </div>
        )}
        {/* Hidden audio element for playback */}
        {blobUrl && (
          <audio
            ref={audioRef}
            src={blobUrl}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            className="hidden"
            data-testid="voice-audio-el"
          />
        )}
        {/* Hidden backing-track element played during recording */}
        {backingSrc && (
          <audio ref={backingRef} src={backingSrc} preload="auto" className="hidden" data-testid="voice-backing-audio" />
        )}
      </div>

      {/* Result */}
      {state === "graded" && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-5 border border-border rounded-2xl bg-background" data-testid="voice-result-card">
            <p className="overline-label">Accuracy</p>
            <p className="mt-1 font-serif text-5xl">{Math.round((accuracy || 0) * 100)}%</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {accuracy >= 0.8 ? "Locked in. Beautiful." : accuracy >= 0.5 ? "Getting there — one more pass." : "A few words drifted. Try again slowly."}
            </p>
          </div>
          <div className="p-5 border border-border rounded-2xl bg-muted" data-testid="voice-transcript-card">
            <p className="overline-label">What we heard</p>
            <p className="mt-2 font-serif text-lg leading-relaxed whitespace-pre-wrap">
              {transcript || <span className="text-muted-foreground italic">Silence.</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
